import React, { Suspense } from 'react'
import { createRoot } from 'react-dom/client'
import { Router } from '@reach/router'
import serverData, { init as initServerData } from 'ppbl/utils/server-data'
import { PAYPAL_THEME } from '@paypalcorp/pp-react'
import { ThemeProvider } from '@emotion/react'
import { ContextProvider as WorldReadyContextProvider } from '@paypalcorp/worldready-react'
import {
  PPBLCampaignRedirectionHelper,
} from 'ppbl/components/CampaignRouters'
import CookieBanner from 'ppbl/components/common/CookieBanner'
import { ApolloProvider } from '@apollo/client'
import graphqlClient from 'ppbl/graphql/graphqlClient'
import { getCreditApplicationDataQuery } from 'ppbl/graphql/queries'
import './index.css'
import Application from 'ppbl/screens/application'
import MaintenancePage from 'ppbl/screens/maintenance-page'
import Start from 'ppbl/screens/start'
import { ApplicationContextProvider } from './context/appContext'
import CampaignRedirectionPage from './screens/campaign-redirection-page'
import { isEmpty } from 'lodash-es'
import CleverCalculator from 'ppbl/screens/cleverCalculator-page'
import DatadogInitializer from 'ppbl/components/DatadogInitializer'
import {
  APPLICATION_PATH,
  DIGITAL_PATH,
  MAINTENANCE_PAGE_PATH,
  PARTNER_PATH,
  PRIVACY_POLICY_PAGE_PATH,
  START_PATH,
  PPBL_REDIRECT,
  CLEVER_CALCULATOR,
  TERMS_AND_CONDITIONS_PATH,
} from './utils/constants'

if (isEmpty(serverData)) {
  initServerData()
}

graphqlClient.writeQuery({
  query: getCreditApplicationDataQuery,
  data: {
    id: null,
    legalBusinessName: null,
    firstName: null,
    lastName: null,
    phoneNumber: null,
    phoneNumberType: null,
    emailAddress: null,
    primaryLoanPurpose: null,
    howDidYouHearAboutThisProduct: null,
    agreedToTermsAndConditions: null,
    homeAddress1: null,
    homeAddress2: null,
    city: null,
    state: null,
    zip: null,
    personalPhoneNumber: null,
    personalPhoneNumberType: null,
    homeBasedBusiness: null,
    businessAddress1: null,
    businessAddress2: null,
    businessCity: null,
    businessState: null,
    businessZip: null,
    businessPhoneNumber: null,
    businessWebAddress: null,
    businessEntityType: null,
    doingBusinessAs: null,
    doingBusinessAsRadio: null,
    stateOfIncorporation: null,
    totalAnnualBusinessRevenue: null,
    businessStartDate: null,
    numberOfFullTimeEmployees: null,
    businessIndustry: null,
    businessSubIndustry: null,
    dateOfBirth: null,
    socialSecurityNumber: null,
    federalTaxId: null,
    percentageOfOwnership: null,
  },
})

let rootElm = document.getElementById('root')

if (!rootElm) {
  rootElm = document.createElement('div')
  document.body.appendChild(rootElm)
}

const LandingPageComponent = React.lazy(() =>
  import('ppbl/screens/landing-page'),
)

const PrivacyPolicyPageComponent = React.lazy(() =>
  import('ppbl/screens/privacy-page'),
)

const TermsAndConditionsComponent = React.lazy(() =>
  import('ppbl/screens/termsAndConditions-page'),
)

const LandingPage = () => (
  <Suspense fallback={<div />}>
    <LandingPageComponent />
  </Suspense>
)

const PrivacyPolicyPage = () => (
  <Suspense fallback={<div />}>
    <PrivacyPolicyPageComponent />
  </Suspense>
)

const TermsAndConditionsPage = () => (
  <Suspense fallback={<div />}>
    <TermsAndConditionsComponent />
  </Suspense>
)

const root = createRoot(rootElm)
root.render(
  <ThemeProvider theme={PAYPAL_THEME}>
    <WorldReadyContextProvider locale={serverData.locality.locale}>
      <ApplicationContextProvider>
        <ApolloProvider client={graphqlClient}>
          <Router
            className="document-body-root"
            basepath={serverData.requestURI}
          >
            <Start path={START_PATH} />
            <PrivacyPolicyPage path={PRIVACY_POLICY_PAGE_PATH} />
            <TermsAndConditionsPage path={TERMS_AND_CONDITIONS_PATH} />
            <Application path={APPLICATION_PATH} />
            <MaintenancePage path={MAINTENANCE_PAGE_PATH} />
            <PPBLCampaignRedirectionHelper path={PPBL_REDIRECT} />
            <CampaignRedirectionPage path={PARTNER_PATH} />
            <CampaignRedirectionPage path={DIGITAL_PATH} />
            <CleverCalculator path={CLEVER_CALCULATOR} />
            <LandingPage path="/" />
          </Router>
          <CookieBanner />
          {serverData?.datadogApplicationId &&
            serverData?.datadogClientToken && (
              <DatadogInitializer
                applicationId={serverData.datadogApplicationId}
                clientToken={serverData.datadogClientToken}
                userId={serverData?.user?.accountNumber}
              />
            )}
        </ApolloProvider>
      </ApplicationContextProvider>
    </WorldReadyContextProvider>
  </ThemeProvider>,
)
